import { useSeo } from "../lib/seo";
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MapPin } from "lucide-react";
import { getTours } from "../lib/adventureApi";
import RALayout from "../components/ra/RALayout";
import TourCard from "../components/ra/TourCard";
import NotFound from "./NotFound";

const U = (id) => `https://images.unsplash.com/${id}?q=80&w=2000&auto=format&fit=crop`;

export const DESTINATIONS = {
  nepal: {
    name: "Nepal",
    hero: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?q=80&w=2000&auto=format&fit=crop",
    tagline: "The roof of the world",
    intro:
      "Home to eight of the world's fourteen 8,000-metre giants, Nepal is where the great adventure began. From the classic trail to Everest Base Camp to the hidden kingdoms of Mustang and the wildlife of Chitwan, this is the beating heart of Himalayan travel — warm, wild and utterly unforgettable.",
  },
  tibet: {
    name: "Tibet",
    hero: "/api/media/wp/2025/03/Devotees-hiking-to-Mt.-Kailash.png",
    tagline: "The sacred plateau",
    intro:
      "A high, holy land of turquoise lakes, whitewashed monasteries and the most sacred mountain on Earth. Our overland journeys to Mount Kailash and Lake Manasarovar follow age-old pilgrim trails across the vast Tibetan plateau — a profound, once-in-a-lifetime experience.",
  },
  bhutan: {
    name: "Bhutan",
    hero: U("photo-1762698336057-05c3260fcd17"),
    tagline: "The last Himalayan kingdom",
    intro:
      "The Kingdom of the Thunder Dragon measures success in happiness, not headcount. Ride or walk over prayer-flag passes, hike to the cliff-clinging Tiger's Nest and lose yourself in a culture that has guarded its traditions — and its forests — like nowhere else on Earth.",
  },
  india: {
    name: "India — The Himalaya",
    hero: U("photo-1762696782497-2b39df0f4523"),
    tagline: "Spiti, Ladakh & the high passes",
    intro:
      "From the apple valleys of Himachal to the lunar deserts of Spiti and Ladakh, Indian Himalaya delivers the highest motorable passes on Earth and a Buddhist culture centuries in the making. Big skies, ancient gompas and roads that dreams are made of.",
  },
  kyrgyzstan: {
    name: "Kyrgyzstan",
    hero: U("photo-1689788648053-cd482d1acd9c"),
    tagline: "The celestial mountains",
    intro:
      "Central Asia's best-kept secret — a nomad's country of alpine lakes, endless meadows and Silk Road caravanserais. Ride between yurt camps beneath the Tien Shan and Pamir ranges and taste a way of life that has barely changed in centuries.",
  },
  pakistan: {
    name: "Pakistan",
    hero: U("photo-1562913346-61ae3ab9277e"),
    tagline: "The Karakoram",
    intro:
      "Where three of the world's great mountain ranges collide, the legendary Karakoram Highway threads through fairy-tale valleys like Hunza and Skardu to the roof of the Silk Road at Khunjerab Pass. Towering peaks, warm welcomes and adventure at its rawest.",
  },
};

export default function Destination() {
  const { slug } = useParams();
  const dest = DESTINATIONS[slug];
  useSeo({ title: dest && `${dest.name || slug} tours & treks | Responsible Adventures`, description: dest && `Small-group adventures in ${dest.name || slug} with Responsible Adventures.` });
  const [tours, setTours] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTours(null);
    getTours({ destination: slug }).then(setTours).catch(() => setTours([]));
  }, [slug]);

  if (!dest) return <NotFound />;

  return (
    <RALayout>
      <div className="relative h-[56vh] min-h-[380px] overflow-hidden" data-testid="destination-hero">
        <img src={dest.hero} alt={dest.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/10" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-6xl mx-auto w-full">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-2 font-mono text-xs tracking-[0.25em] uppercase text-white/70 mb-2">
            <MapPin className="w-3.5 h-3.5" /> {dest.tagline}
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-extrabold text-4xl sm:text-6xl text-white tracking-tight leading-none" data-testid="destination-name">
            {dest.name}
          </motion.h1>
        </div>
      </div>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <p className="text-lg text-ink-secondary leading-relaxed">{dest.intro}</p>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16">
        <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-8">Journeys in {dest.name.split(" — ")[0]}</h2>
        {tours === null ? (
          <div className="flex items-center text-ink-secondary py-16"><Loader2 className="w-5 h-5 animate-spin mr-2 text-terracotta" /> Loading…</div>
        ) : tours.length === 0 ? (
          <p className="text-ink-muted py-10" data-testid="destination-empty">New journeys coming soon — <Link to="/contact" className="text-terracotta hover:underline">enquire</Link> for a bespoke trip.</p>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="destination-tours">
            {tours.map((t, i) => <TourCard key={t.slug} tour={t} index={i} />)}
          </div>
        )}
      </section>
    </RALayout>
  );
}
